import { useEffect, useMemo, useRef, useState, type CSSProperties, type FormEvent, type ReactNode } from "react";
import { SAFE, applyMove, applyRoll, branchCell, chooseBotMove, homeCell, isBranch, isHome, legalMoves, type Color, type GameEvent, type MatchState, type Move, type Token, YARD } from "@ludo/engine";
import { AudioFeedback, vibrate } from "../game/feedback.js";
import { createOfflineMatch, defaultOfflineSetup, offlineDie, type LocalSeatType, type OfflineMatchSetup, type OfflineSeatSetup } from "../game/offline.js";
import { authApi, roomsApi, tournamentApi, type PrivateRoomConfig, type TournamentConfig, type RoomReservation, type Session } from "../api/http.js";
import { LudoRoomClient, type RoomLobby } from "../realtime/roomClient.js";
import { VoiceChatManager } from "../realtime/voice.js";
import { ParticleBurst } from "./Particles.js";
import { trackEvent, flushSessionDuration } from "../analytics.js";

const names = ["Ruby", "Azure", "Jade", "Gold"];
const colors = ["#c62828", "#1565c0", "#2e7d32", "#e68a00"];
const ring: ReadonlyArray<readonly [number, number]> = [
  [6,1],[6,2],[6,3],[6,4],[6,5],
  [5,6],[4,6],[3,6],[2,6],[1,6],[0,6],
  [0,7],
  [0,8],[1,8],[2,8],[3,8],[4,8],[5,8],
  [6,9],[6,10],[6,11],[6,12],[6,13],[6,14],
  [7,14],
  [8,14],[8,13],[8,12],[8,11],[8,10],[8,9],
  [9,8],[10,8],[11,8],[12,8],[13,8],[14,8],
  [14,7],
  [14,6],[13,6],[12,6],[11,6],[10,6],[9,6],
  [8,5],[8,4],[8,3],[8,2],[8,1],[8,0],
  [7,0],
  [6,0]
];
type Point = { x: number; y: number };
type Motion = { tokenId: string; path: Point[]; step: number; captures: string[]; finishes: boolean; color: Color };
type Burst = { id: number; point: Point; color: string };
type CaptureFlight = { id: string; color: Color; from: Point; to: Point };
type Screen = "splash" | "menu" | "setup" | "online" | "how-to" | "settings" | "statistics" | "profile" | "match" | "victory";
type SetupMode = "quick" | "practice";
type SessionStats = { played: number; wins: number; captures: number };
type MatchSource = "offline" | "online";
type RecentRoom = { code: string; visitedAt: number };
type OnlineRoom = { code: string; host: boolean; lobby: RoomLobby | null; connection: "connecting" | "connected" | "reconnecting" | "disconnected"; error: string | null };

const sessionStorageKey = "parchisi-royale-session";
const recentRoomsStorageKey = "parchisi-royale-recent-rooms";
const localProfilesKey = "parchisi-royale-profiles";

type LocalProfile = { username?: string; avatarDataUrl?: string };

function readLocalProfiles(): Record<string, LocalProfile> {
  try { const raw = window.localStorage.getItem(localProfilesKey); return raw ? JSON.parse(raw) : {}; } catch { return {}; }
}

function writeLocalProfiles(value: Record<string, LocalProfile>){
  try { window.localStorage.setItem(localProfilesKey, JSON.stringify(value)); } catch {}
}

function applyLocalProfileToSession(sess: Session | null): Session | null {
  if (!sess) return null;
  try {
    const profiles = readLocalProfiles();
    const override = profiles[sess.user.id];
    if (!override) return sess;
    const username = typeof override.username === "string" && override.username.trim().length > 0 ? override.username : sess.user.username;
    return { ...sess, user: { ...sess.user, username } };
  } catch { return sess; }
}

function readStoredSession(): Session | null {
  try {
    const raw = window.localStorage.getItem(sessionStorageKey);
    return raw ? JSON.parse(raw) as Session : null;
  } catch { return null; }
}

function readRecentRooms(): RecentRoom[] {
  try {
    const raw = window.localStorage.getItem(recentRoomsStorageKey);
    const rooms = raw ? JSON.parse(raw) as unknown : [];
    return Array.isArray(rooms) ? rooms.filter((room): room is RecentRoom => typeof room === "object" && room !== null && "code" in room && typeof room.code === "string" && "visitedAt" in room && typeof room.visitedAt === "number").slice(0, 5) : [];
  } catch { return []; }
}

function inviteCodeFromLocation(): string {
  if (typeof window === "undefined") return "";
  const code = new URLSearchParams(window.location.search).get("room")?.replace(/\D/g, "") ?? "";
  return code.length === 6 ? code : "";
}

export function App() {
  const [screen, setScreen] = useState<Screen>("splash");
  const [setupMode, setSetupMode] = useState<SetupMode>("quick");
  const [offlineSetup, setOfflineSetup] = useState<OfflineMatchSetup>(() => defaultOfflineSetup());
  const [matchSource, setMatchSource] = useState<MatchSource>("offline");
  const [session, setSession] = useState<Session | null>(() => applyLocalProfileToSession(readStoredSession()));
  const [recentRooms, setRecentRooms] = useState<RecentRoom[]>(readRecentRooms);
  const [onlineRoom, setOnlineRoom] = useState<OnlineRoom | null>(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [hapticsEnabled, setHapticsEnabled] = useState(true);
  const [exitNotice, setExitNotice] = useState(false);
  const [stats, setStats] = useState<SessionStats>({ played: 0, wins: 0, captures: 0 });
  const [game, setGame] = useState<MatchState>(() => createOfflineMatch());
  const [motion, setMotion] = useState<Motion | null>(null);
  const [rolling, setRolling] = useState(false);
  const [diceFace, setDiceFace] = useState<number | null>(null);
  const [lastRoll, setLastRoll] = useState<number[] | null>(null);
  const [rollId, setRollId] = useState(0);
  const [bursts, setBursts] = useState<Burst[]>([]);
  const [captureFlights, setCaptureFlights] = useState<CaptureFlight[]>([]);
  const [turnFlash, setTurnFlash] = useState(0);
  const [selectedDieIndex, setSelectedDieIndex] = useState<number>(0);
  const [rewardToast, setRewardToast] = useState<{ type: "extra-turn" | "penalty"; title: string; subtitle: string; icon: string; key: number } | null>(null);
  const timers = useRef<number[]>([]);
  const recordedMatch = useRef<string | null>(null);
  const audio = useRef(new AudioFeedback());
  const roomClient = useRef(new LudoRoomClient());
  const voiceManager = useRef<any>(null);
  const current = game.players.find((player) => player.seat === game.turn);
  const onlineSeat = matchSource === "online" && session ? game.players.find((player) => player.userId === session.user.id)?.seat : undefined;
  const humanTurn = matchSource === "online" ? onlineSeat === game.turn : current?.status === "human";

  // Current dice values available
  const currentDice = useMemo(() => {
    if (game.dice && game.dice.length > 0) return game.dice;
    if (game.die) return [game.die];
    return [];
  }, [game.dice, game.die]);

  // When game enters move phase, auto-select a playable die if current has none
  useEffect(() => {
    if (game.phase === "roll") {
      setSelectedDieIndex(0);
    } else if (game.phase === "move" && currentDice.length > 1) {
      const d0Moves = legalMoves(game, currentDice[0]!);
      const d1Moves = legalMoves(game, currentDice[1]!);
      if (!d0Moves.length && d1Moves.length > 0) {
        setSelectedDieIndex(1);
      } else if (d0Moves.length > 0 && selectedDieIndex === 1 && !d1Moves.length) {
        setSelectedDieIndex(0);
      }
    }
  }, [game.phase, game.turn, currentDice]);

  // Active die for moving
  const activeDie = currentDice[selectedDieIndex] ?? currentDice[0] ?? game.die ?? 0;

  // Moves available for current active die (or fallback)
  const moves = useMemo(() => {
    if (game.phase !== "move") return [];
    const directMoves = legalMoves(game, activeDie);
    if (directMoves.length > 0) return directMoves;
    if (currentDice.length > 1) {
      const otherDie = currentDice[selectedDieIndex === 0 ? 1 : 0]!;
      return legalMoves(game, otherDie);
    }
    return [];
  }, [game, activeDie, currentDice, selectedDieIndex]);

  const selectable = humanTurn && !motion && !rolling ? moves.map((move) => move.tokenId) : [];
  const hidden = useMemo(() => new Set([...(motion ? [motion.tokenId, ...motion.captures] : []), ...captureFlights.map((flight) => flight.id)]), [motion, captureFlights]);
  const activeName = current?.displayName || names[game.turn] || "Player";
  const actionCopy = rolling ? "Rolling the dice" : game.phase === "over" ? "Match complete" : game.phase === "move" ? humanTurn ? currentDice.length > 1 ? "Pick a die or tap a piece" : "Choose a glowing piece" : "Choosing a move" : humanTurn ? "Tap Roll to play" : "Waiting for the roll";
  const addBurst = (point: Point, color: string) => setBursts((items) => [...items, { id: Date.now() + Math.random(), point, color }]);
  const giveHaptic = (pattern: number | number[]) => { if (hapticsEnabled) vibrate(pattern); };
  const showRewardToast = (type: "extra-turn" | "penalty", title: string, subtitle: string, icon: string) => {
    setRewardToast({ type, title, subtitle, icon, key: Date.now() });
    timers.current.push(window.setTimeout(() => setRewardToast(null), 2400));
  };

  const onSelectDie = (index: number) => {
    if (game.phase !== "move" || !humanTurn || rolling || motion) return;
    if (currentDice[index] !== undefined) {
      audio.current.play("select");
      setSelectedDieIndex(index);
      giveHaptic(18);
    }
  };

  useEffect(() => {
    trackEvent("page_view", { screen, source: matchSource });
  }, [screen, matchSource]);

  useEffect(() => () => {
    flushSessionDuration();
    timers.current.forEach(window.clearTimeout);
    roomClient.current.disconnect();
  }, []);
  useEffect(() => { audio.current.setEnabled(soundEnabled); }, [soundEnabled]);
  useEffect(() => {
    if (screen !== "splash") return;
    const id = window.setTimeout(() => setScreen("menu"), 1700);
    return () => window.clearTimeout(id);
  }, [screen]);
  useEffect(() => {
    const inviteCode = inviteCodeFromLocation();
    if (screen === "menu" && inviteCode) {
      trackEvent("invite_joined", { room_code_length: inviteCode.length, source: "query_param" });
      setScreen("online");
    }
  }, [screen]);
  useEffect(() => { if (screen === "match") { audio.current.play("turn"); setTurnFlash(Date.now()); } }, [game.turn, screen]);
  useEffect(() => {
    if (matchSource !== "offline" || screen !== "match" || !current?.bot || motion || rolling || game.phase === "over") return;
    const id = window.setTimeout(() => {
      if (game.phase === "roll") startRoll(true);
      else { const choice = chooseBotMove(game); if (choice) startMove(choice, true); }
    }, game.phase === "roll" ? 700 : 900);
    return () => window.clearTimeout(id);
  }, [current?.bot, game, matchSource, motion, rolling, screen]);
  useEffect(() => {
    if (matchSource === "online" && game.lastRoll) setLastRoll(game.lastRoll.dice);
  }, [game.lastRoll, matchSource]);
  useEffect(() => {
    if (screen !== "match" || game.phase !== "over" || motion) return;
    const id = window.setTimeout(() => {
      if (recordedMatch.current !== game.id) {
        recordedMatch.current = game.id;
        const winnerSeat = game.finished[0] ?? 0;
        const winCount = winnerSeat === (matchSource === "online" ? onlineSeat : 0) ? 1 : 0;
        setStats((currentStats) => ({
          ...currentStats,
          played: currentStats.played + 1,
          wins: currentStats.wins + winCount,
        }));
        trackEvent("game_finish", { winner_seat: winnerSeat, source: matchSource, player_count: game.players.filter((player) => player.status !== "empty").length, did_player_win: winCount === 1 });
        trackEvent("match_complete", { winner_seat: winnerSeat, source: matchSource, player_count: game.players.filter((player) => player.status !== "empty").length, did_player_win: winCount === 1 });
      }
      setScreen("victory");
    }, 720);
    return () => window.clearTimeout(id);
  }, [game.finished, game.id, game.phase, matchSource, motion, onlineSeat, screen]);

  function startRoll(force = false) {
    if (rolling || motion || game.phase !== "roll" || (!humanTurn && !force)) return;
    // clear any pending timers and start a single synchronized roll
    timers.current.forEach(window.clearTimeout);
    timers.current.length = 0;
    const ROLL_DURATION = 820;
    audio.current.play("diceRoll");
    const nextRoll = rollId + 1;
    setRollId(nextRoll);
    setRolling(true);
    trackEvent("dice_roll", { source: matchSource, turn: game.turn, roll_id: nextRoll });

    if (matchSource === "online") {
      // send authoritative roll to server and stop the local animation after the duration
      roomClient.current.roll();
      timers.current.push(window.setTimeout(() => {
        setRolling(false);
        audio.current.play("diceStop");
        giveHaptic(35);
      }, ROLL_DURATION));
      return;
    }

    // offline: compute deterministic dice, apply immediately but only reveal after the roll animation
    const d1 = offlineDie();
    const d2 = offlineDie();
    const rollResult = applyRoll(game, [d1, d2], `offline:${game.rollNonce}`, Date.now());
    const result = rollResult.state;
    timers.current.push(window.setTimeout(() => {
      setGame(result);
      setLastRoll(result.lastRoll?.dice ?? [d1, d2]);
      setRolling(false);
      audio.current.play("diceStop");
      giveHaptic(35);
      addBurst({ x: 90, y: 48 }, "#f6bd42");
      const extraTurnEv = rollResult.events.find((e): e is Extract<GameEvent, { t: "extraTurn" }> => e.t === "extraTurn");
      const penaltyEv = rollResult.events.find((e): e is Extract<GameEvent, { t: "pass" }> => e.t === "pass" && e.reason === "triple_doubles");
      if (extraTurnEv) {
        showRewardToast("extra-turn", "⭐ EXTRA TURN", extraTurnEv.reason === "doubles" ? "Doubles!" : extraTurnEv.reason === "capture" ? "Capture!" : "Piece reached home!", extraTurnEv.reason === "doubles" ? "🎲" : extraTurnEv.reason === "capture" ? "⚔️" : "🏆");
      } else if (penaltyEv) {
        showRewardToast("penalty", "⚠️ THREE DOUBLES", "Turn penalty!", "⚠️");
      }
    }, ROLL_DURATION));
  }

  function startMove(move: Move, force = false) {
    if (motion || rolling || game.phase !== "move" || (!humanTurn && !force)) return;
    const token = game.tokens.find((candidate) => candidate.id === move.tokenId);
    if (!token) return;
    audio.current.play("select");
    trackEvent("move_token", { token_id: move.tokenId, color: token.color, captures: move.captures.length, finishes: move.finishes, source: matchSource });
    if (matchSource === "online") {
      roomClient.current.move(move.tokenId);
      return;
    }
    const applyResult = applyMove(game, move.tokenId, Date.now(), activeDie);
    const next = applyResult.state;
    const path = [token.cell, ...move.path].map((cell) => pointForCell(cell, token.color, token.id));
    setGame(next);
    setMotion({ tokenId: token.id, path, step: 0, captures: move.captures, finishes: move.finishes, color: token.color });
    const advance = (step: number) => {
      if (step >= path.length - 1) {
        const destination = path.at(-1)!;
        if (move.captures.length) {
          audio.current.play("capture");
          giveHaptic([22, 38, 55]);
          setStats((currentStats) => ({ ...currentStats, captures: currentStats.captures + move.captures.length }));
          addBurst(destination, "#fff2b2");
          const flights = move.captures.map((id) => {
            const defeated = game.tokens.find((candidate) => candidate.id === id)!;
            return { id, color: defeated.color, from: destination, to: yardPoint(defeated.color, Number(id.split(":").at(-1) ?? 0)) };
          });
          setCaptureFlights((active) => [...active, ...flights]);
          timers.current.push(window.setTimeout(() => setCaptureFlights((active) => active.filter((flight) => !move.captures.includes(flight.id))), 590));
        }
        if (move.finishes) { audio.current.play("home"); addBurst(destination, colors[token.color] ?? "#ffffff"); }
        if (next.phase === "over") { audio.current.play("victory"); giveHaptic([40, 55, 80]); addBurst({ x: 50, y: 50 }, "#f6bd42"); }
        const extraTurnEv = applyResult.events.find((e): e is Extract<GameEvent, { t: "extraTurn" }> => e.t === "extraTurn");
        if (extraTurnEv) {
          showRewardToast("extra-turn", "⭐ EXTRA TURN", extraTurnEv.reason === "doubles" ? "Doubles!" : extraTurnEv.reason === "capture" ? "Capture!" : "Piece reached home!", extraTurnEv.reason === "doubles" ? "🎲" : extraTurnEv.reason === "capture" ? "⚔️" : "🏆");
        }
        timers.current.push(window.setTimeout(() => setMotion(null), 150));
        return;
      }
      timers.current.push(window.setTimeout(() => {
        setMotion((active) => active ? { ...active, step: step + 1 } : null);
        audio.current.play("move");
        advance(step + 1);
      }, 145));
    };
    advance(0);
  }

  const onToken = (id: string) => {
    const move = moves.find((candidate) => candidate.tokenId === id);
    if (move && humanTurn) startMove(move);
    else audio.current.play("invalid");
  };
  const resetMatch = (setup: OfflineMatchSetup = offlineSetup) => {
    setRolling(false);
    setMotion(null);
    setDiceFace(null);
    setLastRoll(null);
    setBursts([]);
    setCaptureFlights([]);
    const nextGame = createOfflineMatch(setup);
    setGame(nextGame);
    trackEvent("game_start", { match_source: "offline", player_count: nextGame.players.filter((player) => player.status !== "empty").length });
  };
  const startMatch = (setup: OfflineMatchSetup = offlineSetup) => { roomClient.current.disconnect(); setOnlineRoom(null); setMatchSource("offline"); audio.current.play("button"); recordedMatch.current = null; setOfflineSetup(setup); resetMatch(setup); trackEvent("match_start", { source: "offline", player_count: setup.filter((seat) => seat.type !== "empty").length }); setScreen("match"); };
  const returnToMenu = () => { roomClient.current.disconnect(); setOnlineRoom(null); setMatchSource("offline"); audio.current.play("button"); resetMatch(); setScreen("menu"); };
  const openSetup = (mode: SetupMode) => { audio.current.play("button"); setSetupMode(mode); setScreen("setup"); };
  const rememberRoom = (code: string) => setRecentRooms((currentRooms) => {
    const next = [{ code, visitedAt: Date.now() }, ...currentRooms.filter((room) => room.code !== code)].slice(0, 5);
    window.localStorage.setItem(recentRoomsStorageKey, JSON.stringify(next));
    return next;
  });
  const saveSession = (nextSession: Session | null) => {
    // apply local overrides when saving session
    const toStore = nextSession ? applyLocalProfileToSession(nextSession) : null;
    setSession(toStore);
    if (toStore) {
      window.localStorage.setItem(sessionStorageKey, JSON.stringify(toStore));
      trackEvent("profile_updated", { user_id: toStore.user.id, scope: "session" });
    } else {
      window.localStorage.removeItem(sessionStorageKey);
      trackEvent("profile_updated", { scope: "signed_out" });
    }
  };
  const connectToRoom = async (reservation: RoomReservation, code: string, host: boolean) => {
    setOnlineRoom({ code, host, lobby: null, connection: "connecting", error: null });
    setMatchSource("online");
    try {
       // create voice manager instance (will not start mic automatically)
       voiceManager.current = new VoiceChatManager((payload: unknown) => roomClient.current.sendVoiceSignal(payload), (count: number) => {
         /* peer count changed - UI reads voiceManager.getPeers() directly */
       });

       await roomClient.current.connect(
         reservation,
         (state) => {
            setGame(state);
            if (state.phase !== "waiting") {
              trackEvent("match_start", { source: "online", player_count: state.players.filter((p) => p.status !== "empty").length });
              setScreen("match");
            }
         },
         (status) => setOnlineRoom((room) => room ? status === "reconnecting" ? { ...room, connection: "reconnecting", error: null } : status === "reconnected" ? { ...room, connection: "connected", error: null } : { ...room, connection: "disconnected", error: status } : room),
         (lobby) => setOnlineRoom((room) => room ? { ...room, lobby } : room),
         // onVoice handler -> route to voice manager
         (payload: unknown) => void voiceManager.current?.handleSignal(payload),
       );
       setOnlineRoom((room) => room ? { ...room, connection: "connected" } : room);
    } catch {
       setOnlineRoom((room) => room ? { ...room, connection: "disconnected", error: "connection_failed" } : room);
    }
  };
  const createOnlineRoom = async (players: number) => {
    if (!session) { setScreen("profile"); return; }
    const config: PrivateRoomConfig = {
      hostSeat: 0,
      seats: [0, 1, 2, 3].map((seat) => ({ seat: seat as Color, status: seat < players ? "human" as const : "empty" as const, displayName: seat === 0 ? session.user.username : `Player ${seat + 1}` })),
    };
    try {
      const created = await roomsApi.createPrivate(session.token, config);
      trackEvent("room_created", { room_code_length: created.roomCode.length, player_count: players });
      rememberRoom(created.roomCode);
      await connectToRoom(created.reservation, created.roomCode, true);
    } catch (error) {
      setOnlineRoom({ code: "", host: false, lobby: null, connection: "disconnected", error: error instanceof Error ? error.message : "room_creation_failed" });
    }
  };
  const createOnlineTournament = async () => {
    if (!session) { setScreen("profile"); return; }
    const config: TournamentConfig = {
      title: `${session.user.username}'s tournament`,
      hostSeat: 0,
      seats: [0, 1, 2, 3].map((seat) => ({ seat: seat as Color, status: seat === 0 ? "human" : "empty", displayName: seat === 0 ? session.user.username : `Seat ${seat + 1}` })),
    };
    try {
      const created = await tournamentApi.create(session.token, config);
      rememberRoom(created.tournamentCode);
      await connectToRoom(created.reservation, created.tournamentCode, true);
    } catch (error) {
      setOnlineRoom({ code: "", host: false, lobby: null, connection: "disconnected", error: error instanceof Error ? error.message : "tournament_creation_failed" });
    }
  };
  const joinOnlineRoom = async (code: string) => {
    if (!session) { setScreen("profile"); return; }
    const normalized = code.replace(/\D/g, "");
    if (normalized.length !== 6) { setOnlineRoom({ code: "", host: false, lobby: null, connection: "disconnected", error: "Enter a six-digit room code" }); return; }
    try {
      const joined = await roomsApi.joinPrivate(session.token, normalized);
      trackEvent("room_joined", { room_code_length: normalized.length });
      rememberRoom(normalized);
      await connectToRoom(joined.reservation, normalized, false);
    } catch (error) {
      setOnlineRoom({ code: normalized, host: false, lobby: null, connection: "disconnected", error: error instanceof Error ? error.message : "room_join_failed" });
    }
  };
  const joinOnlineTournament = async (code: string) => {
    if (!session) { setScreen("profile"); return; }
    const normalized = code.replace(/\D/g, "");
    if (normalized.length !== 6) { setOnlineRoom({ code: "", host: false, lobby: null, connection: "disconnected", error: "Enter a six-digit tournament code" }); return; }
    try {
      const joined = await tournamentApi.join(session.token, normalized);
      trackEvent("room_joined", { room_code_length: normalized.length, type: "tournament" });
      rememberRoom(normalized);
      await connectToRoom(joined.reservation, normalized, false);
    } catch (error) {
      setOnlineRoom({ code: normalized, host: false, lobby: null, connection: "disconnected", error: error instanceof Error ? error.message : "tournament_join_failed" });
    }
  };
  const canRoll = humanTurn && game.phase === "roll" && !rolling && !motion;

  if (screen === "splash") return <SplashScreen onContinue={() => { audio.current.play("button"); setScreen("menu"); }} />;
  if (screen === "menu") return <MainMenu session={session} onOnline={() => setScreen("online")} onPlay={() => openSetup("quick")} onPractice={() => openSetup("practice")} onNavigate={setScreen} onExit={() => setExitNotice(true)} exitNotice={exitNotice} onDismissExit={() => setExitNotice(false)} />;
  if (screen === "setup") return <SetupScreen initialMode={setupMode} onBack={() => setScreen("menu")} onStart={startMatch} />;
  if (screen === "online") return <OnlineRoomsScreen session={session} recentRooms={recentRooms} room={onlineRoom} inviteCode={inviteCodeFromLocation()} onBack={returnToMenu} onCreate={createOnlineRoom} onCreateTournament={createOnlineTournament} onJoin={joinOnlineRoom} onJoinTournament={joinOnlineTournament} onStart={() => roomClient.current.start()} onProfile={() => setScreen("profile")} voiceManagerRef={voiceManager} />;
  if (screen === "how-to") return <HowToScreen onBack={() => setScreen("menu")} />;
  if (screen === "settings") return <SettingsScreen soundEnabled={soundEnabled} hapticsEnabled={hapticsEnabled} onSound={() => setSoundEnabled((value) => !value)} onHaptics={() => setHapticsEnabled((value) => !value)} onBack={() => setScreen("menu")} />;
  if (screen === "statistics") return <StatisticsScreen stats={stats} onBack={() => setScreen("menu")} />;
  if (screen === "profile") return <ProfileScreen stats={stats} session={session} onSession={saveSession} onBack={() => setScreen("menu")} />;
  if (screen === "victory") return <VictoryScreen game={game} stats={stats} onPlayAgain={() => { setSetupMode("quick"); startMatch(); }} onMenu={returnToMenu} />;

  return <main className={`game-shell ${turnFlash ? "turn-flash" : ""}`} data-seat={game.turn}>
    {rewardToast && (
      <div className={`reward-toast ${rewardToast.type === "penalty" ? "reward-toast--penalty" : ""}`} key={rewardToast.key} role="status" aria-live="assertive">
        <span className="reward-toast__icon">{rewardToast.icon}</span>
        <div className="reward-toast__body">
          <strong>{rewardToast.title}</strong>
          <small>{rewardToast.subtitle}</small>
        </div>
      </div>
    )}

    {/* ── TOP BAR ── */}
    <header className="gs-topbar">
      <button className="gs-brand tap" aria-label="Return to main menu" onClick={returnToMenu}>
        <img src="/icons/blue-horse-logo.png" alt="Parchisi Royale" className="gs-brand-logo" />
        <span className="gs-brand-title">PARCHISI <strong>ROYALE</strong></span>
      </button>

      <div className="gs-turn-indicator" data-seat={game.turn} aria-live="polite">
        <span className="gs-turn-light" aria-hidden="true" />
        <span className="gs-turn-label">{game.turn === 0 ? "YOUR TURN" : `${activeName.toUpperCase()}'S TURN`}</span>
      </div>

      <div className="gs-mode-chip">{matchSource === "online" ? `ROOM ${onlineRoom?.code ?? ""}` : "OFFLINE"}</div>
    </header>

    {/* ── MAIN GAME PLAY LAYOUT ── */}
    <div className="gs-layout-container">
      {/* ── BOARD (HERO) ── */}
      <div className="gs-board-zone">
        <span className="gs-board-aura" aria-hidden="true" />
        <Board game={game} selected={selectable} hidden={hidden} motion={motion} bursts={bursts} captureFlights={captureFlights} onToken={onToken} onBurstDone={(id) => setBursts((items) => items.filter((item) => item.id !== id))} />
      </div>

      {/* ── CONTROL DOCK ── */}
      <section className="gs-dock" aria-label="Game controls">
        {/* Player cards row */}
        <div className="gs-players" aria-label="Players">
          {game.players.map((player) => <PlayerPanel game={game} key={player.seat} seat={player.seat} active={player.seat === game.turn} />)}
        </div>

        {/* Action prompt + Dice + Roll button */}
        <div className="gs-controls">
          <div className="gs-action-copy">
            <p className="gs-action-eyebrow">CURRENT ACTION</p>
            <strong className="gs-action-text">{actionCopy}</strong>
            {lastRoll && <span className="gs-last-roll">Last Roll: <b>{lastRoll.join(" + ")}</b></span>}
          </div>

          <div className="gs-dice-area">
            <Dice
              value={rolling ? null : (lastRoll ?? (game.lastRoll?.dice ?? (game.dice?.length ? game.dice : (game.die ? [game.die] : null))))}
              rolling={rolling}
              color={game.turn}
              rollId={rollId}
              selectedDieIndex={selectedDieIndex}
              onSelectDie={onSelectDie}
              interactive={humanTurn && game.phase === "move"}
            />
          </div>

          <button
            id="roll-dice-btn"
            className={`gs-roll-btn tap ${canRoll ? "gs-roll-btn--active" : ""} ${rolling ? "gs-roll-btn--rolling" : ""}`}
            disabled={!canRoll}
            onClick={() => startRoll()}
            aria-label={rolling ? "Rolling dice" : canRoll ? "Roll dice" : "Wait for your turn"}
          >
            <span className="gs-roll-btn__label">{rolling ? "ROLLING…" : game.phase === "move" && humanTurn ? "SELECT PIECE" : "ROLL DICE"}</span>
            <small className="gs-roll-btn__sub">{humanTurn ? "YOUR TURN" : "BOT TURN"}</small>
          </button>
        </div>

        {/* Footer strip */}
        <div className="gs-footer">
          <span className="gs-connection">
            <i className="gs-conn-dot" aria-hidden="true" />
            {matchSource === "online" ? `LIVE ROOM ${onlineRoom?.code ?? ""}` : `LOCAL TABLE · ${game.players.filter((p) => p.status === "human").length}H ${game.players.filter((p) => p.status === "bot").length}B`}
          </span>
          <button className="gs-new-match tap" onClick={() => { setSetupMode("quick"); setScreen("setup"); }}>New match</button>
        </div>
      </section>
    </div>
  </main>;
}

function SplashScreen({ onContinue }: { onContinue: () => void }) {
  return <main className="flow-shell splash-screen">
    <AnimatedBackdrop />
    <div className="splash-mark" aria-hidden="true">
      <img src="/icons/blue-horse-logo.png" alt="PARCHISI ROYALE" className="splash-logo-img" />
    </div>
    <section className="splash-copy"><p>ORIGINAL BOARD GAME</p><h1>PARCHISI <strong>ROYALE</strong></h1><span>Make every roll count.</span></section>
    <button className="splash-continue tap" onClick={onContinue}>TAP TO CONTINUE</button>
  </main>;
}

function MainMenu({ session, onOnline, onPlay, onPractice, onNavigate, onExit, exitNotice, onDismissExit }: { session: Session | null; onOnline: () => void; onPlay: () => void; onPractice: () => void; onNavigate: (screen: Screen) => void; onExit: () => void; exitNotice: boolean; onDismissExit: () => void }) {
  const localProfiles = typeof window !== 'undefined' ? readLocalProfiles() : {};
  const local = localProfiles['local'] ?? {};
  const avatarUrl = session ? localProfiles[session.user.id]?.avatarDataUrl ?? local.avatarDataUrl : local.avatarDataUrl;
  const displayName = session ? (localProfiles[session.user.id]?.username ?? session.user.username ?? (local.username ?? 'Guest Player')) : (local.username ?? 'Guest Player');
  const label = session ? 'ONLINE PROFILE' : 'LOCAL PROFILE';
  return <main className="flow-shell menu-screen">
    <AnimatedBackdrop />
    <header className="menu-topbar"><button className="guest-profile tap" onClick={() => onNavigate("profile")}>
      {avatarUrl ? <img src={avatarUrl} alt="avatar" /> : <span>{displayName.slice(0,1).toUpperCase()}</span>}
      <div><b>{displayName}</b><small>{label}</small></div></button><button className="top-text-button tap" onClick={() => onNavigate("settings")}>SETTINGS</button></header>
    <section className="menu-hero">
      <p className="hero-kicker">THE CLASSIC RACE, REIMAGINED</p>
      <div className="menu-brand-header">
        <img src="/icons/parchisi-royale-brand.png" alt="PARCHISI ROYALE" className="menu-brand-img" />
      </div>
      <p className="hero-subtitle">A sharp, social board game made for fast, satisfying turns.</p>
    </section>
    <nav className="menu-actions" aria-label="Main menu">
      <button className="menu-online tap" onClick={() => { trackEvent("invite_sent", { source: "menu" }); onOnline(); }}><span><small>PLAY WITH FRIENDS</small>PLAY ONLINE</span><i aria-hidden="true">◎</i></button>
      <button className="menu-play tap" onClick={() => { trackEvent("game_start", { match_source: "local" }); onPlay(); }}><span><small>LOCAL TABLE</small>PLAY LOCAL</span><i aria-hidden="true"><AppIcon kind="play" /></i></button>
      <button className="menu-practice tap" onClick={() => { trackEvent("game_start", { match_source: "practice" }); onPractice(); }}><span><small>SOLO TABLE</small>PRACTICE</span><i aria-hidden="true"><AppIcon kind="practice" /></i></button>
      <div className="menu-utility-grid">
        <button className="utility-button tap" onClick={() => onNavigate("how-to")}><AppIcon kind="help" /><span><b>HOW TO PLAY</b><small>Rules and tips</small></span></button>
        <button className="utility-button tap" onClick={() => onNavigate("statistics")}><AppIcon kind="stats" /><span><b>STATISTICS</b><small>Session progress</small></span></button>
        <button className="utility-button tap" onClick={() => onNavigate("profile")}><AppIcon kind="profile" /><span><b>PROFILE</b><small>{displayName}</small></span></button>
        <button className="utility-button tap" onClick={() => onNavigate("settings")}><AppIcon kind="settings" /><span><b>SETTINGS</b><small>Sound and haptics</small></span></button>
      </div>
      <FooterLinks />
      <button className="exit-button tap" onClick={onExit}>EXIT GAME</button>
    </nav>
    {exitNotice && <div className="flow-toast" role="status"><span>In a browser, close this tab to exit Parchisi Royale.</span><button onClick={onDismissExit} aria-label="Dismiss">OK</button></div>}
  </main>;
}

function FooterLinks() {
  return <div className="legal-links">
    <a href="/privacy-policy" target="_blank" rel="noreferrer">Privacy Policy</a>
    <a href="/terms-of-service" target="_blank" rel="noreferrer">Terms</a>
    <a href="/contact" target="_blank" rel="noreferrer">Contact</a>
    <a href="/cookie-policy" target="_blank" rel="noreferrer">Cookie Notice</a>
  </div>;
}

const seatLabels = ["Ruby", "Azure", "Jade", "Gold"] as const;
const seatColorLabels = ["Red", "Blue", "Green", "Yellow"] as const;
const botDifficulties = ["easy", "medium", "hard", "expert"] as const;

function SetupScreen({ initialMode, onBack, onStart }: { initialMode: SetupMode; onBack: () => void; onStart: (setup: OfflineMatchSetup) => void }) {
  const [seats, setSeats] = useState<OfflineSeatSetup[]>(() => defaultOfflineSetup(initialMode));
  const playerCount = seats.filter((seat) => seat.type !== "empty").length;
  const humanCount = seats.filter((seat) => seat.type === "human").length;
  const botCount = seats.filter((seat) => seat.type === "bot").length;
  const updateSeat = (seatNumber: Color, update: Partial<OfflineSeatSetup>) => setSeats((currentSeats) => currentSeats.map((seat) => seat.seat === seatNumber ? { ...seat, ...update } : seat));
  const setPlayerCount = (count: number) => setSeats((currentSeats) => currentSeats.map((seat, index) => {
    if (index >= count) return { ...seat, type: "empty" };
    if (seat.type !== "empty") return seat;
    return { ...seat, type: index === 0 ? "human" : "bot", name: index === 0 ? "You" : seatLabels[index] ?? `Player ${index + 1}`, difficulty: "medium" };
  }));

  return <FlowPage title="Match setup" subtitle="Choose every seat. Nothing is forced onto your table." onBack={onBack}>
    <div className="setup-banner"><span>LOCAL TABLE</span><strong>Set player count, roles, names, and bot difficulty.</strong></div>
    <section className="player-count-picker" aria-label="Number of active players">
      <div><span>NUMBER OF PLAYERS</span><strong>{playerCount} / 4</strong></div>
      <div className="count-options">{[1, 2, 3, 4].map((count) => <button className={`tap ${playerCount === count ? "selected" : ""}`} onClick={() => setPlayerCount(count)} key={count} aria-pressed={playerCount === count}>{count}</button>)}</div>
    </section>
    <section className="seat-list" aria-label="Seat configuration">
      {seats.map((seat) => <article className={`seat-config seat-${seat.seat} ${seat.type === "empty" ? "is-empty" : ""}`} key={seat.seat}>
        <header><i className="seat-swatch" aria-hidden="true" /><div><span>SEAT {seat.seat + 1}</span><strong>{seatLabels[seat.seat]} · {seatColorLabels[seat.seat]}</strong></div><small>DEFAULT AVATAR</small></header>
        <div className="seat-role-picker" role="group" aria-label={`${seatLabels[seat.seat]} player type`}>
          {(["human", "bot", "empty"] as LocalSeatType[]).map((type) => <button className={`tap ${seat.type === type ? "selected" : ""}`} onClick={() => updateSeat(seat.seat, { type })} key={type} aria-pressed={seat.type === type}>{type === "human" ? "Human" : type === "bot" ? "Bot" : "Empty"}</button>)}
        </div>
        {seat.type !== "empty" && <label className="seat-name"><span>NAME</span><input value={seat.name} maxLength={18} onChange={(event) => updateSeat(seat.seat, { name: event.target.value })} aria-label={`${seatLabels[seat.seat]} name`} /></label>}
        {seat.type === "bot" && <div className="difficulty-picker"><span>DIFFICULTY</span><div>{botDifficulties.map((difficulty) => <button className={`tap ${seat.difficulty === difficulty ? "selected" : ""}`} onClick={() => updateSeat(seat.seat, { difficulty })} key={difficulty} aria-pressed={seat.difficulty === difficulty}>{difficulty}</button>)}</div></div>}
        {seat.type === "empty" && <p className="empty-seat-copy">This colour is out of this local match.</p>}
      </article>)}
    </section>
    <div className="setup-details"><span>TABLE READY</span><p>{humanCount ? `${humanCount} local human${humanCount === 1 ? "" : "s"} and ${botCount} bot${botCount === 1 ? "" : "s"}. Empty seats stay out of the match.` : "Add at least one local human to control this table."}</p></div>
    <button className="flow-primary-button tap" disabled={!humanCount} onClick={() => onStart(seats)}>START MATCH <small>{playerCount === 1 ? "SOLO PRACTICE TABLE" : `${playerCount}-PLAYER LOCAL TABLE`}</small></button>
  </FlowPage>;
}

function OnlineRoomsScreen({ session, recentRooms, room, inviteCode, onBack, onCreate, onCreateTournament, onJoin, onJoinTournament, onStart, onProfile, voiceManagerRef }: { session: Session | null; recentRooms: RecentRoom[]; room: OnlineRoom | null; inviteCode: string; onBack: () => void; onCreate: (players: number) => Promise<void>; onCreateTournament: () => Promise<void>; onJoin: (code: string) => Promise<void>; onJoinTournament: (code: string) => Promise<void>; onStart: () => void; onProfile: () => void; voiceManagerRef?: React.RefObject<any> }) {
  const [players, setPlayers] = useState(2);
  const [code, setCode] = useState(inviteCode);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => { if (inviteCode) setCode(inviteCode); }, [inviteCode]);
  const submit = async (action: () => Promise<void>) => {
    setBusy(true);
    setNotice(null);
    try { await action(); } finally { setBusy(false); }
  };
  const inviteLink = room?.code && typeof window !== "undefined" ? `${window.location.origin}/?room=${room.code}` : "";
  const copyInvite = async () => {
    try { await navigator.clipboard.writeText(room?.code ?? ""); setNotice("Room code copied."); }
    catch { setNotice("Copy is unavailable in this browser. Share the six-digit code manually."); }
  };
  const shareInvite = async () => {
    if (!room?.code) return;
    const shareMethod = typeof navigator.share === "function" ? "native" : "copy";
    const text = `Join my Parchisi Royale room: ${room.code}`;
    trackEvent("invite_sent", { room_code_length: room.code.length, method: shareMethod });
    try {
      if (shareMethod === "native") await navigator.share({ title: "Parchisi Royale", text, url: inviteLink });
      else { await navigator.clipboard.writeText(`${text} ${inviteLink}`); setNotice("Invite link copied."); }
    } catch { setNotice("Invite sharing was cancelled or unavailable."); }
  };

  if (!session) return <FlowPage title="Play online" subtitle="Sign in once to create and join private rooms." onBack={onBack}>
    <div className="online-account-card"><span>PRIVATE ROOMS</span><strong>Your online profile keeps your seat and reconnects you after a short drop.</strong><p>Use Profile to create a free game identity, then return here to play with friends.</p></div>
    <button className="flow-primary-button tap" onClick={onProfile}>OPEN PROFILE <small>SIGN IN OR CREATE ACCOUNT</small></button>
  </FlowPage>;

  if (room?.code) {
    const lobby = room.lobby;
    const canStart = Boolean(room.host && lobby && lobby.seats.every((seat) => seat.status !== "human" || seat.occupied));
    const voicePeers = voiceManagerRef?.current ? voiceManagerRef.current.getPeers() : [];
    return <FlowPage title="Online room" subtitle="Share the code, wait for friends, then start together." onBack={onBack}>
      <section className="room-code-card"><span>PRIVATE ROOM CODE</span><strong>{room.code}</strong><div><button className="room-mini-button tap" onClick={() => void copyInvite()}>COPY</button><button className="room-mini-button tap" onClick={() => void shareInvite()}>INVITE</button></div></section>
      <section className="room-lobby-card" aria-live="polite">
        <header><span>{lobby?.started ? "MATCH STARTING" : "WAITING FOR PLAYERS"}</span><b className={`room-connection ${room.connection}`}>{room.connection === "connected" ? "CONNECTED" : room.connection === "reconnecting" ? "RECONNECTING" : room.connection.toUpperCase()}</b></header>
        <p className="room-ready-count">{lobby ? `${lobby.seats.filter((seat) => seat.occupied).length} / ${lobby.seats.length} READY` : "Connecting to the authoritative room…"}</p>
        <div className="lobby-seat-list">{lobby?.seats.map((seat) => <div className={`lobby-seat ${seat.occupied ? "ready" : "waiting"}`} key={seat.seat}><i className={`lobby-color seat-${seat.seat}`} /><span><b>{seat.displayName || `Seat ${seat.seat + 1}`}</b><small>{seat.status === "bot" ? "BOT" : seat.occupied ? "CONNECTED" : "WAITING"}</small></span><em>{seat.occupied ? "READY" : "OPEN"}</em></div>) ?? <div className="lobby-loading">Connecting to your room…</div>}</div>

        <div style={{ marginTop: 12 }}>
          <div className="voice-controls">
            <strong>Voice Chat</strong>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              {!voiceManagerRef?.current || !voiceManagerRef.current.isStarted() ? <button className="room-mini-button tap" onClick={async () => { try { await voiceManagerRef?.current?.start(); } catch (err) { setNotice("Voice connection failed"); } }}>JOIN VOICE</button> : <button className="room-mini-button tap" onClick={async () => { await voiceManagerRef?.current?.stop(); }}>LEAVE VOICE</button>}
              <button className="room-mini-button tap" onClick={() => { if (voiceManagerRef?.current) voiceManagerRef.current.setMuted(!voiceManagerRef.current.isMuted()); }}>{voiceManagerRef?.current && voiceManagerRef.current.isMuted() ? "UNMUTE" : "MUTE"}</button>
            </div>
            <div style={{ marginTop: 8 }}>
              <small>Connected peers: {voicePeers.length}</small>
              <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                {voicePeers.map((p: any) => <div key={p.id} className="voice-peer" style={{ padding: 6, border: "1px solid rgba(255,255,255,0.06)", borderRadius: 6 }}><span style={{ display: "block", fontSize: 12 }}>{p.id}</span><div style={{ height: 6, width: 60, background: "rgba(255,255,255,0.06)", marginTop: 6 }}><div style={{ height: 6, width: `${Math.round((p.level ?? 0) * 100)}%`, background: "#4ade80" }} /></div></div>)}
              </div>
            </div>
          </div>
        </div>
      </section>
      {room.error && <p className="online-error">{room.error.replaceAll("_", " ")}</p>}
      {notice && <p className="online-notice">{notice}</p>}
      {room.host ? <button className="flow-primary-button tap" disabled={!canStart} onClick={onStart}>START MATCH <small>{canStart ? "EVERY PLAYER IS READY" : "WAITING FOR FRIENDS"}</small></button> : <div className="setup-details"><span>YOU ARE IN</span><p>The host starts once every selected player has joined.</p></div>}
    </FlowPage>;
  }

  return <FlowPage title="Play online" subtitle="Create a private table or join a friend with a room code." onBack={onBack}>
    {room?.error && <p className="online-error">{room.error.replaceAll("_", " ")}</p>}
    <section className="online-create-card"><span>CREATE ROOM</span><strong>Choose your table size</strong><div className="online-player-count">{[2, 3, 4].map((count) => <button className={`tap ${players === count ? "selected" : ""}`} onClick={() => setPlayers(count)} key={count} aria-pressed={players === count}>{count}<small>PLAYERS</small></button>)}</div><button className="flow-primary-button tap" disabled={busy} onClick={() => void submit(() => onCreate(players))}>CREATE PRIVATE ROOM <small>GET A 6-DIGIT CODE</small></button></section>
    <section className="online-create-card"><span>CREATE TOURNAMENT</span><strong>Reserve a private tournament room</strong><button className="flow-secondary-button tap" disabled={busy} onClick={() => void submit(onCreateTournament)}>CREATE TOURNAMENT <small>HOST A FRIENDLY CUP</small></button></section>
    <section className="online-join-card"><span>JOIN ROOM</span><strong>Enter your friend&apos;s code</strong><input inputMode="numeric" pattern="[0-9]*" maxLength={6} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))} placeholder="000000" aria-label="Six digit room code" /><button className="room-join-button tap" disabled={busy || code.length !== 6} onClick={() => void submit(() => onJoin(code))}>JOIN ROOM</button><button className="room-join-button tap" disabled={busy || code.length !== 6} onClick={() => void submit(() => onJoinTournament(code))}>JOIN TOURNAMENT</button></section>
    {recentRooms.length > 0 && <section className="recent-rooms"><span>RECENT ROOMS</span>{recentRooms.map((recent) => <button className="recent-room tap" key={recent.code} disabled={busy} onClick={() => void submit(() => onJoin(recent.code))}><b>{recent.code}</b><small>JOIN AGAIN</small></button>)}</section>}
    {notice && <p className="online-notice">{notice}</p>}
  </FlowPage>;
}

function HowToScreen({ onBack }: { onBack: () => void }) {
  const steps = [
    ["1", "Roll to leave", "Roll a six on either die to bring a piece out of your yard."],
    ["2", "Race around", "Move clockwise around the ring and into your color lane."],
    ["3", "Capture smart", "Land on an opponent to send that piece back to its yard and earn an extra turn."],
    ["4", "Reach home", "Guide all four pieces home safely before the other players."]
  ];
  return <FlowPage title="How to play" subtitle="Everything you need for a winning match in Parchisi Royale." onBack={onBack}>
    <div className="rules-card">
      <span className="rules-safe">SAFE SPACES</span>
      <p>Star-marked cells and colored entry squares are safe. Pieces resting there cannot be captured.</p>
    </div>
    <ol className="rule-list">
      {steps.map(([number, title, copy]) => (
        <li key={number}>
          <span>{number}</span>
          <div>
            <b>{title}</b>
            <p>{copy}</p>
          </div>
        </li>
      ))}
    </ol>
    <section className="extra-turns-rules" aria-label="Extra turns rules">
      <h2 className="rules-section-title">🎲 Extra Turns</h2>
      <div className="reward-rule-item">
        <span className="reward-rule-icon">🎲</span>
        <div>
          <strong>Roll Doubles</strong>
          <p>Roll matching numbers on both dice (1+1, 2+2, 3+3, 4+4, 5+5, 6+6) to earn an extra turn.</p>
        </div>
      </div>
      <div className="reward-rule-item">
        <span className="reward-rule-icon">⚔️</span>
        <div>
          <strong>Capture</strong>
          <p>Capture an opponent's piece to earn an extra turn.</p>
        </div>
      </div>
      <div className="reward-rule-item">
        <span className="reward-rule-icon">🏆</span>
        <div>
          <strong>Reach Home</strong>
          <p>Successfully bring one of your pieces home to earn an extra turn.</p>
        </div>
      </div>
      <div className="penalty-card">
        <span className="penalty-tag">⚠️ THREE DOUBLES PENALTY</span>
        <p>Rolling doubles three times consecutively during the same turn sequence ends your turn immediately, and your most recently moved piece is sent back to the yard.</p>
      </div>
    </section>
  </FlowPage>;
}

function SettingsScreen({ soundEnabled, hapticsEnabled, onSound, onHaptics, onBack }: { soundEnabled: boolean; hapticsEnabled: boolean; onSound: () => void; onHaptics: () => void; onBack: () => void }) {
  return <FlowPage title="Settings" subtitle="Set the feel of your local game." onBack={onBack}>
    <div className="settings-list"><SettingRow label="Sound effects" copy="Dice, pieces, captures, and victory" enabled={soundEnabled} onToggle={onSound} /><SettingRow label="Haptic feedback" copy="Subtle vibration when your device supports it" enabled={hapticsEnabled} onToggle={onHaptics} /></div>
    <div className="settings-note"><b>DISPLAY</b><p>Parchisi Royale respects your system reduced-motion preference automatically.</p></div>
  </FlowPage>;
}

function SettingRow({ label, copy, enabled, onToggle }: { label: string; copy: string; enabled: boolean; onToggle: () => void }) {
  return <div className="setting-row"><div><b>{label}</b><p>{copy}</p></div><button className={`switch ${enabled ? "on" : ""}`} type="button" role="switch" aria-checked={enabled} aria-label={label} onClick={onToggle}><i /></button></div>;
}

function StatisticsScreen({ stats, onBack }: { stats: SessionStats; onBack: () => void }) {
  const winRate = stats.played ? Math.round(stats.wins / stats.played * 100) : 0;
  return <FlowPage title="Statistics" subtitle="Your local Parchisi Royale session." onBack={onBack}>
    <div className="stat-grid"><StatCard value={stats.played} label="Matches" /><StatCard value={stats.wins} label="Wins" /><StatCard value={`${winRate}%`} label="Win rate" /><StatCard value={stats.captures} label="Captures" /></div>
    <div className="progress-card"><span>ROYAL RANK</span><strong>{stats.wins >= 5 ? "Crown Chaser" : stats.wins >= 1 ? "Rising Royal" : "First Roll"}</strong><p>Complete matches to build your local record.</p></div>
  </FlowPage>;
}

const AVATAR_GALLERY = [
  { id: "knight", name: "Knight", svg: `data:image/svg+xml;utf8,${encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><circle cx='50' cy='50' r='47' fill='#0d122b' stroke='#d4a853' stroke-width='3'/><circle cx='50' cy='50' r='43' fill='none' stroke='#d4a853' stroke-width='0.75' stroke-dasharray='3 2'/><path d='M35 74 h30 c0-15-5-25-10-30 c5-5 5-15-2-20 c-6-4-13-2-16 4 c-2 4-1 10-6 12 c-5 2-8 8-8 14 c5 0 8-3 10-3 c-1 5 1 10 3 13 c-5 3-7 6-1 10 z' fill='#f0d48a' stroke='#8b6d2e' stroke-width='1.5'/><circle cx='46' cy='32' r='2.5' fill='#0d122b'/></svg>`)}` },
  { id: "crown", name: "Crown", svg: `data:image/svg+xml;utf8,${encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><circle cx='50' cy='50' r='47' fill='#0d122b' stroke='#d4a853' stroke-width='3'/><circle cx='50' cy='50' r='43' fill='none' stroke='#d4a853' stroke-width='0.75' stroke-dasharray='3 2'/><path d='M25 68 h50 l-6-34 l-12 16 l-7-24 l-7 24 l-12-16 z' fill='#f0d48a' stroke='#8b6d2e' stroke-width='1.5'/><circle cx='50' cy='24' r='3.5' fill='#fcd34d'/><circle cx='31' cy='33' r='3' fill='#fcd34d'/><circle cx='69' cy='33' r='3' fill='#fcd34d'/><rect x='27' y='64' width='46' height='5' rx='2' fill='#d4a853'/></svg>`)}` },
  { id: "dice", name: "Dice", svg: `data:image/svg+xml;utf8,${encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><circle cx='50' cy='50' r='47' fill='#0d122b' stroke='#d4a853' stroke-width='3'/><circle cx='50' cy='50' r='43' fill='none' stroke='#d4a853' stroke-width='0.75' stroke-dasharray='3 2'/><rect x='24' y='32' width='26' height='26' rx='5' fill='#f0d48a' stroke='#8b6d2e' stroke-width='1.5' transform='rotate(-8 37 45)'/><circle cx='33' cy='41' r='2' fill='#1e1b4b'/><circle cx='41' cy='49' r='2' fill='#1e1b4b'/><rect x='48' y='40' width='26' height='26' rx='5' fill='#fef08a' stroke='#8b6d2e' stroke-width='1.5' transform='rotate(12 61 53)'/><circle cx='55' cy='47' r='1.8' fill='#1e1b4b'/><circle cx='67' cy='47' r='1.8' fill='#1e1b4b'/><circle cx='61' cy='53' r='1.8' fill='#1e1b4b'/><circle cx='55' cy='59' r='1.8' fill='#1e1b4b'/><circle cx='67' cy='59' r='1.8' fill='#1e1b4b'/></svg>`)}` },
  { id: "pawn", name: "Pawn", svg: `data:image/svg+xml;utf8,${encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><circle cx='50' cy='50' r='47' fill='#0d122b' stroke='#d4a853' stroke-width='3'/><circle cx='50' cy='50' r='43' fill='none' stroke='#d4a853' stroke-width='0.75' stroke-dasharray='3 2'/><circle cx='50' cy='32' r='11' fill='#f0d48a' stroke='#8b6d2e' stroke-width='1.5'/><path d='M44 43 h12 c0 14 8 20 12 28 h-36 c4-8 12-14 12-28 z' fill='#f0d48a' stroke='#8b6d2e' stroke-width='1.5'/><rect x='28' y='71' width='44' height='5' rx='2.5' fill='#d4a853'/></svg>`)}` },
  { id: "shield", name: "Shield", svg: `data:image/svg+xml;utf8,${encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><circle cx='50' cy='50' r='47' fill='#0d122b' stroke='#d4a853' stroke-width='3'/><circle cx='50' cy='50' r='43' fill='none' stroke='#d4a853' stroke-width='0.75' stroke-dasharray='3 2'/><path d='M30 28 h40 c0 24-8 38-20 46 c-12-8-20-22-20-46 z' fill='#f0d48a' stroke='#8b6d2e' stroke-width='1.5'/><path d='M50 36 l3 8 h9 l-7 5 l3 9 l-8-6 l-8 6 l3-9 l-7-5 h9 z' fill='#0d122b'/></svg>`)}` },
  { id: "lion", name: "Lion", svg: `data:image/svg+xml;utf8,${encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><circle cx='50' cy='50' r='47' fill='#0d122b' stroke='#d4a853' stroke-width='3'/><circle cx='50' cy='50' r='43' fill='none' stroke='#d4a853' stroke-width='0.75' stroke-dasharray='3 2'/><circle cx='50' cy='50' r='23' fill='#d4a853'/><circle cx='50' cy='50' r='16' fill='#fef08a'/><circle cx='43' cy='46' r='2.2' fill='#0d122b'/><circle cx='57' cy='46' r='2.2' fill='#0d122b'/><polygon points='50,52 46,57 54,57' fill='#8b6d2e'/><path d='M46 59 q4 4 8 0' stroke='#8b6d2e' stroke-width='1.5' fill='none'/></svg>`)}` },
  { id: "wreath", name: "Wreath", svg: `data:image/svg+xml;utf8,${encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><circle cx='50' cy='50' r='47' fill='#0d122b' stroke='#d4a853' stroke-width='3'/><circle cx='50' cy='50' r='43' fill='none' stroke='#d4a853' stroke-width='0.75' stroke-dasharray='3 2'/><path d='M32 68 c-8-12-6-28 6-36 c-2 8 2 16 8 20 c-8 2-12 8-14 16 z M68 68 c8-12 6-28-6-36 c2 8-2 16-8 20 c8 2 12 8 14 16 z' fill='#f0d48a' stroke='#8b6d2e' stroke-width='1'/><circle cx='50' cy='46' r='6' fill='#d4a853'/><path d='M44 72 h12 l-6-6 z' fill='#fcd34d'/></svg>`)}` },
  { id: "eagle", name: "Eagle", svg: `data:image/svg+xml;utf8,${encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><circle cx='50' cy='50' r='47' fill='#0d122b' stroke='#d4a853' stroke-width='3'/><circle cx='50' cy='50' r='43' fill='none' stroke='#d4a853' stroke-width='0.75' stroke-dasharray='3 2'/><path d='M50 25 l5 8 l16-6 l-6 16 l14 4 l-12 12 l8 16 l-16-6 l-9 10 l-9-10 l-16 6 l8-16 l-12-12 l14-4 l-6-16 l16 6 z' fill='#f0d48a' stroke='#8b6d2e' stroke-width='1.2'/><circle cx='50' cy='50' r='6' fill='#0d122b'/><circle cx='50' cy='50' r='3' fill='#fcd34d'/></svg>`)}` }
];

function ProfileScreen({ stats, session, onSession, onBack }: { stats: SessionStats; session: Session | null; onSession: (session: Session | null) => void; onBack: () => void }) {
  const [mode, setMode] = useState<"login" | "register">("login");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [localUsername, setLocalUsername] = useState("");
  const [avatarDataUrl, setAvatarDataUrl] = useState<string | null>(null);

  useEffect(() => {
    try {
      const profiles = readLocalProfiles();
      if (session) {
        const p = profiles[session.user.id] ?? {};
        setLocalUsername(p.username ?? session.user.username ?? "");
        setAvatarDataUrl(p.avatarDataUrl ?? null);
      } else {
        const p = profiles['local'] ?? {};
        setLocalUsername(p.username ?? "");
        setAvatarDataUrl(p.avatarDataUrl ?? null);
      }
    } catch {}
  }, [session]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const next = mode === "login" ? await authApi.login(email, password) : await authApi.register(username, email, password);
      trackEvent("profile_created", { method: mode, user_id: next.user.id });
      onSession(next);
      setPassword("");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message.replaceAll("_", " ") : "Unable to sign in");
    } finally { setBusy(false); }
  };

  const name = session?.user.username ?? (localUsername || "Guest Player");

  const handleAvatarUpload = (file: File | null) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setAvatarDataUrl(String(reader.result));
      setSaveSuccess(false);
    };
    reader.readAsDataURL(file);
  };

  const saveLocalProfile = () => {
    try {
      const profiles = readLocalProfiles();
      const nextUsername = localUsername && localUsername.trim().length > 0 ? localUsername.trim() : undefined;
      if (session) {
        const existing = profiles[session.user.id] ?? {};
        profiles[session.user.id] = { ...existing, ...(nextUsername ? { username: nextUsername } : {}), avatarDataUrl: avatarDataUrl ?? existing.avatarDataUrl };
        trackEvent("profile_updated", { user_id: session.user.id, has_avatar: Boolean(avatarDataUrl) });
      } else {
        const existing = profiles['local'] ?? {};
        profiles['local'] = { ...existing, ...(nextUsername ? { username: nextUsername } : {}), avatarDataUrl: avatarDataUrl ?? existing.avatarDataUrl };
        trackEvent("profile_updated", { scope: "local", has_avatar: Boolean(avatarDataUrl) });
      }
      writeLocalProfiles(profiles);
      if (session) onSession(applyLocalProfileToSession(session));
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2500);
    } catch (e) { console.error(e); }
  };

  const winRate = stats.played ? Math.round((stats.wins / stats.played) * 100) : 0;

  return (
    <FlowPage title="Player Profile" subtitle="Your identity across local and friend matches." onBack={onBack}>
      {/* Hero Profile Card */}
      <section className="profile-hero-card">
        <div className="profile-avatar-wrapper">
          {avatarDataUrl ? (
            <img src={avatarDataUrl} alt="avatar" className="profile-avatar-img" />
          ) : (
            <div className="profile-avatar-fallback">{name.slice(0, 1).toUpperCase()}</div>
          )}
          <span className="avatar-gold-ring" aria-hidden="true" />
        </div>
        <div className="profile-hero-meta">
          <span className="profile-rank-kicker">{session ? "ONLINE RACER" : "LOCAL RACER"}</span>
          <h2>{name}</h2>
          <span className="profile-title-chip">{stats.wins >= 5 ? "Crown Sovereign" : stats.wins >= 1 ? "Rising Royal" : "First Roll"}</span>
        </div>
      </section>

      {/* Session Record Bar */}
      <div className="profile-record-bar">
        <div className="record-item">
          <b>{stats.wins}</b>
          <span>WINS</span>
        </div>
        <div className="record-divider" />
        <div className="record-item">
          <b>{stats.played}</b>
          <span>MATCHES</span>
        </div>
        <div className="record-divider" />
        <div className="record-item">
          <b>{winRate}%</b>
          <span>WIN RATE</span>
        </div>
      </div>

      {/* Identity Customization Card */}
      <section className="profile-section-card">
        <div className="section-card-header">
          <span>IDENTITY</span>
          <strong>Customize Appearance</strong>
        </div>

        <div className="avatar-picker-group">
          <label className="picker-label">CHOOSE ROYAL AVATAR</label>
          <div className="avatar-gallery-grid">
            {AVATAR_GALLERY.map((g) => {
              const isSelected = avatarDataUrl === g.svg;
              return (
                <button
                  key={g.id}
                  type="button"
                  className={`avatar-medallion tap ${isSelected ? "selected" : ""}`}
                  onClick={() => { setAvatarDataUrl(g.svg); setSaveSuccess(false); }}
                  aria-label={`Select ${g.name} avatar`}
                  title={g.name}
                >
                  <img src={g.svg} alt={g.name} />
                  {isSelected && <span className="medallion-check">✓</span>}
                </button>
              );
            })}
          </div>

          <div className="avatar-upload-row">
            <label className="custom-upload-button tap">
              <span>Upload Custom Photo</span>
              <input
                type="file"
                accept="image/*"
                className="hidden-file-input"
                onChange={(e) => handleAvatarUpload(e.target.files?.[0] ?? null)}
              />
            </label>
            {avatarDataUrl && (
              <button
                type="button"
                className="avatar-reset-button tap"
                onClick={() => { setAvatarDataUrl(null); setSaveSuccess(false); }}
              >
                Reset
              </button>
            )}
          </div>
        </div>

        <label className="profile-input-field">
          <span>DISPLAY NAME</span>
          <input
            value={localUsername}
            placeholder={session?.user.username ?? "Guest Player"}
            minLength={3}
            maxLength={18}
            onChange={(e) => {
              setLocalUsername(e.target.value.replace(/[^A-Za-z0-9 _-]/g, ""));
              setSaveSuccess(false);
            }}
          />
        </label>

        <div className="profile-actions-row">
          <button type="button" className="flow-primary-button tap" onClick={saveLocalProfile}>
            {saveSuccess ? "✓ SAVED" : "SAVE PROFILE"}
          </button>
          {session && (
            <button type="button" className="profile-signout-link tap" onClick={() => onSession(null)}>
              Sign Out
            </button>
          )}
        </div>
      </section>

      {/* Online Account Card */}
      {!session ? (
        <section className="profile-section-card">
          <div className="section-card-header">
            <span>ONLINE PROFILE</span>
            <strong>Private Rooms & Sync</strong>
          </div>
          <p className="section-card-copy">Create a free account to host 6-digit private rooms and reconnect to games.</p>
          <form className="account-form" onSubmit={submit}>
            <div className="account-mode">
              <button className={mode === "login" ? "selected" : ""} type="button" onClick={() => setMode("login")}>SIGN IN</button>
              <button className={mode === "register" ? "selected" : ""} type="button" onClick={() => setMode("register")}>CREATE ACCOUNT</button>
            </div>
            {mode === "register" && (
              <label className="profile-input-field">
                <span>PLAYER NAME</span>
                <input value={username} minLength={3} maxLength={18} required onChange={(e) => setUsername(e.target.value.replace(/[^A-Za-z0-9 _-]/g, ""))} />
              </label>
            )}
            <label className="profile-input-field">
              <span>EMAIL</span>
              <input type="email" value={email} required onChange={(e) => setEmail(e.target.value)} />
            </label>
            <label className="profile-input-field">
              <span>PASSWORD</span>
              <input type="password" value={password} minLength={8} required onChange={(e) => setPassword(e.target.value)} />
            </label>
            {error && <p className="online-error">{error}</p>}
            <button className="flow-secondary-button tap" disabled={busy}>
              {busy ? "CONNECTING..." : mode === "login" ? "SIGN IN TO ONLINE" : "CREATE ONLINE ACCOUNT"}
            </button>
          </form>
        </section>
      ) : (
        <div className="online-synced-note">
          <span>✓ CONNECTED AS {session.user.username.toUpperCase()}</span>
        </div>
      )}
    </FlowPage>
  );
}

function VictoryScreen({ game, stats, onPlayAgain, onMenu }: { game: MatchState; stats: SessionStats; onPlayAgain: () => void; onMenu: () => void }) {
  const winner = game.finished[0] ?? 0;
  const winnerPlayer = game.players.find((player) => player.seat === winner);
  const localWon = winnerPlayer?.status === "human";
  const winnerName = winnerPlayer?.displayName || names[winner] || "Player";
  return <main className="flow-shell victory-screen">
    <AnimatedBackdrop />
    <div className="victory-rays" aria-hidden="true" /><div className="victory-medal" style={{ "--winner-color": colors[winner] ?? "#f6bd42" } as CSSProperties}>{localWon ? "1" : "!"}</div>
    <section className="victory-copy"><p>{localWon ? "A ROYAL VICTORY" : "MATCH COMPLETE"}</p><h1>{localWon ? "YOU WIN" : `${winnerName.toUpperCase()} WINS`}</h1><span>{localWon ? "Your pieces reached home first." : "Good race. The next crown is yours."}</span></section>
    <div className="victory-score"><span>SESSION WINS</span><strong>{stats.wins}</strong><i /> <span>MATCHES</span><strong>{stats.played}</strong></div>
    <div className="victory-actions"><button className="flow-primary-button tap" onClick={onPlayAgain}>PLAY AGAIN <small>NEW CLASSIC MATCH</small></button><button className="flow-secondary-button tap" onClick={onMenu}>MAIN MENU</button></div>
  </main>;
}

function FlowPage({ title, subtitle, onBack, children }: { title: string; subtitle: string; onBack: () => void; children: ReactNode }) {
  return <main className="flow-shell flow-page"><AnimatedBackdrop /><header className="flow-page-header"><button className="back-button tap" onClick={onBack}>BACK</button><div><p> PARCHISI ROYALE </p><h1>{title}</h1></div></header><p className="flow-page-subtitle">{subtitle}</p><section className="flow-page-content">{children}</section></main>;
}

function StatCard({ value, label }: { value: string | number; label: string }) { return <div className="stat-card"><strong>{value}</strong><span>{label}</span></div>; }

function AnimatedBackdrop() { return <div className="animated-backdrop" aria-hidden="true"><i className="ambient-orb orb-one" /><i className="ambient-orb orb-two" /><i className="ambient-orb orb-three" /><span className="ambient-grid" /></div>; }
function AppIcon({ kind }: { kind: "play" | "practice" | "help" | "stats" | "profile" | "settings" }) { return <i className={`app-icon app-icon-${kind}`} aria-hidden="true" />; }

function PlayerPanel({ game, seat, active }: { game: MatchState; seat: Color; active: boolean }) {
  const player = game.players.find((candidate) => candidate.seat === seat);
  const remaining = game.tokens.filter((token) => token.color === seat && !isHome(token.cell)).length;
  const finished = game.tokens.filter((token) => token.color === seat && isHome(token.cell)).length;
  const name = player?.displayName ?? names[seat] ?? "Player";
  const isBot = Boolean(player?.bot);
  const doubles = player?.consecutiveDoubles ?? 0;
  const isEmpty = player?.status === "empty";
  return <article
    className={`pp-card ${active ? "pp-card--active" : ""} ${isEmpty ? "pp-card--empty" : ""}`}
    style={{ "--pp-color": colors[seat] ?? "#ffffff" } as CSSProperties}
    aria-label={`${name} — ${active ? "active turn" : "waiting"}`}
  >
    <div className="pp-avatar" aria-hidden="true">{name.slice(0, 1).toUpperCase()}</div>
    <div className="pp-info">
      <strong className="pp-name">{name}</strong>
      <span className="pp-role">{isBot ? "BOT" : isEmpty ? "EMPTY" : "HUMAN"}</span>
      {doubles > 0 && <span className="pp-doubles">{doubles}×🎲</span>}
    </div>
    <div className="pp-tokens">
      <b>{remaining}</b>
      <span>LEFT</span>
    </div>
    {active && <span className="pp-active-dot" aria-hidden="true" />}
  </article>;
}

function Dice({
  value,
  rolling,
  color,
  rollId,
  selectedDieIndex = 0,
  onSelectDie,
  interactive = false,
}: {
  value: number | number[] | null;
  rolling: boolean;
  color: Color;
  rollId?: number;
  selectedDieIndex?: number;
  onSelectDie?: (index: number) => void;
  interactive?: boolean;
}) {
  const diceValues = Array.isArray(value) ? value : (value ? [value] : []);
  const pips: Record<number, number[]> = { 0: [], 1: [5], 2: [1,9], 3: [1,5,9], 4: [1,3,7,9], 5: [1,3,5,7,9], 6: [1,3,4,6,7,9] };
  
  const leftValue = rolling ? 0 : diceValues[0] ?? 0;
  const rightValue = rolling ? 0 : diceValues[1] ?? 0;

  const renderDie = (visible: number, slotIndex: number) => {
    const isSelected = selectedDieIndex === slotIndex && !rolling && visible > 0;
    const canClick = interactive && !rolling && visible > 0;
    return (
      <button
        type="button"
        className={`premium-die slot-${slotIndex} ${rolling ? "rolling" : ""} ${visible ? "has-value" : ""} ${isSelected ? "selected-die" : ""} ${canClick ? "interactive-die tap" : ""}`}
        key={`die-slot-${slotIndex}`}
        onClick={() => onSelectDie?.(slotIndex)}
        disabled={!canClick}
        aria-label={`Die ${slotIndex + 1} with value ${visible}${isSelected ? " (Selected)" : ""}`}
      >
        <span className="dice-glint" aria-hidden="true" />
        {pips[visible]?.map((pip) => <i className={`pip pip-${pip}`} key={pip} />)}
        {rolling && <em>?</em>}
      </button>
    );
  };

  return (
    <div key={`dicepair-tray-${rollId ?? (rolling ? "rolling" : "stopped")}`} className={`dice-tray color-${color}`} aria-label={rolling ? "Dice rolling" : `Dice show ${leftValue || "-"} and ${rightValue || "-"}`}>
      <div className="tray-container">
        <div className="tray-base" aria-hidden="true" />
        <div className="tray-slot tray-slot-left">
          {renderDie(leftValue, 0)}
          <div className="slot-highlight" aria-hidden="true" />
        </div>
        <div className="tray-slot tray-slot-right">
          {renderDie(rightValue, 1)}
          <div className="slot-highlight" aria-hidden="true" />
        </div>
      </div>
    </div>
  );
}

function Board({ game, selected, hidden, motion, bursts, captureFlights, onToken, onBurstDone }: { game: MatchState; selected: string[]; hidden: Set<string>; motion: Motion | null; bursts: Burst[]; captureFlights: CaptureFlight[]; onToken: (id: string) => void; onBurstDone: (id: number) => void }) {
  const style = (x: number, y: number) => ({ gridColumn: x + 1, gridRow: y + 1 });
  return (
    <div className="board" data-active={game.turn} aria-label="PARCHISI ROYALE board">
      <div className="board-grid">
        {/* 4 Corner Yards with 4 Recessed Sockets */}
        {[0, 1, 2, 3].map((color) => (
          <div className={`yard yard-${color}`} key={color}>
            <div className="yard-inner-tray">
              {[0, 1, 2, 3].map((slotIdx) => {
                const token = game.tokens.find(
                  (cand) => cand.color === color && cand.cell === YARD && Number(cand.id.split(":").at(-1) ?? 0) === slotIdx
                );
                return (
                  <div className="yard-piece-slot" key={slotIdx}>
                    <div className="yard-socket" aria-hidden="true" />
                    {token && (
                      <Token
                        key={token.id}
                        token={token}
                        hidden={hidden.has(token.id)}
                        selected={selected.includes(token.id)}
                        onClick={() => onToken(token.id)}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Ring Track Cells */}
        {ring.map(([x, y], cell) => (
          <div
            className={`track-cell ${SAFE.has(cell) ? "safe" : ""} entry-${[0, 13, 26, 39].includes(cell) ? [0, 13, 26, 39].indexOf(cell) : "none"}`}
            style={style(x, y)}
            key={cell}
          >
            {SAFE.has(cell) && <b aria-hidden="true" />}
            {game.tokens
              .filter((token) => token.cell === cell)
              .map((token) => (
                <Token
                  key={token.id}
                  token={token}
                  hidden={hidden.has(token.id)}
                  selected={selected.includes(token.id)}
                  onClick={() => onToken(token.id)}
                />
              ))}
          </div>
        ))}

        {/* Home Lanes */}
        {[0, 1, 2, 3].flatMap((color) =>
          Array.from({ length: 5 }, (_, index) => (
            <div
              className={`home-lane lane-${color}`}
              style={style(...branchPosition(color as Color, index))}
              key={`${color}-${index}`}
            >
              {game.tokens
                .filter((token) => token.cell === branchCell(color as Color, index))
                .map((token) => (
                  <Token
                    key={token.id}
                    token={token}
                    hidden={hidden.has(token.id)}
                    selected={selected.includes(token.id)}
                    onClick={() => onToken(token.id)}
                  />
                ))}
            </div>
          ))
        )}

        {/* Central Home Core */}
        <div className="home-core">
          <div className="home-core-triangle home-tri-0" />
          <div className="home-core-triangle home-tri-1" />
          <div className="home-core-triangle home-tri-2" />
          <div className="home-core-triangle home-tri-3" />
          {[0, 1, 2, 3].map((color) => (
            <i className={`finish finish-${color}`} key={color}>
              {game.tokens
                .filter((token) => token.cell === homeCell(color as Color))
                .map((token) => (
                  <Token
                    key={token.id}
                    token={token}
                    hidden={hidden.has(token.id)}
                    selected={false}
                    onClick={() => undefined}
                  />
                ))}
            </i>
          ))}
        </div>

        {/* Motion, Captures, Bursts */}
        {motion && <MotionToken motion={motion} />}
        {captureFlights.map((flight) => (
          <CaptureFlightToken key={flight.id} flight={flight} />
        ))}
        {bursts.map((burst) => (
          <ParticleBurst
            key={burst.id}
            x={burst.point.x}
            y={burst.point.y}
            color={burst.color}
            onDone={() => onBurstDone(burst.id)}
          />
        ))}
      </div>
    </div>
  );
}

function MotionToken({ motion }: { motion: Motion }) { const point = motion.path[motion.step] ?? motion.path.at(-1)!; return <div className="motion-token" style={{ left: `${point.x}%`, top: `${point.y}%` }}><Token token={{ id: motion.tokenId, color: motion.color, cell: 0 }} hidden={false} selected={false} onClick={() => undefined} /></div>; }
function CaptureFlightToken({ flight }: { flight: CaptureFlight }) { return <div className="capture-flight" style={{ left: `${flight.from.x}%`, top: `${flight.from.y}%`, "--flight-x": `${flight.to.x - flight.from.x}%`, "--flight-y": `${flight.to.y - flight.from.y}%` } as CSSProperties}><Token token={{ id: flight.id, color: flight.color, cell: 0 }} hidden={false} selected={false} onClick={() => undefined} /></div>; }
function Token({ token, hidden, selected, onClick }: { token: Token; hidden: boolean; selected: boolean; onClick: () => void }) {
  return (
    <button
      aria-label={`Token ${token.id}`}
      className={`token color-${token.color} ${selected ? "selectable" : ""} ${hidden ? "hidden-token" : ""}`}
      onClick={onClick}
    >
      <span className="token-crown-mark" aria-hidden="true" />
    </button>
  );
}
function branchPosition(color: Color, index: number): [number, number] { const n = index + 1; return color === 0 ? [7, n] : color === 1 ? [7 + n, 7] : color === 2 ? [7, 14 - n] : [7 - n, 7]; }
function pointForCell(cell: number, color: Color, tokenId: string): Point {
  if (cell === YARD) return yardPoint(color, Number(tokenId.split(":").at(-1) ?? 0));
  if (cell >= 0 && cell < ring.length) {
    const [x,y] = ring[cell]!;
    return gridPoint(x,y);
  }
  if (isBranch(cell)) {
    const index = cell - branchCell(color, 0);
    return gridPoint(...branchPosition(color, index));
  }
  if (isHome(cell)) {
    const finish: Record<Color, Point> = {
      0: { x: 50, y: 43 },
      1: { x: 57, y: 50 },
      2: { x: 50, y: 57 },
      3: { x: 43, y: 50 }
    };
    return finish[color];
  }
  return { x: 50, y: 50 };
}
function gridPoint(x: number, y: number): Point { return { x: (x + .5) / 15 * 100, y: (y + .5) / 15 * 100 }; }
function yardPoint(color: Color, token: number): Point { const base: Record<Color, [number,number]> = { 0:[2,2],1:[12,2],2:[12,12],3:[2,12] }; const [x,y] = base[color]; return gridPoint(x + token % 2 * 1.5, y + Math.floor(token / 2) * 1.5); }
